import React, { useRef, useState } from "react";
import { MoreVertical } from "lucide-react";
import { Input } from "@/components/ui/input";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { RemixiconComponentType, RiErrorWarningLine } from "@remixicon/react";

export interface ISidebarItem {
  type: "tab" | "folder" | "button" | "other";
  value: string;
  label: string;
  icon: RemixiconComponentType;
  id: string | null;
  route?: string;
  beta?: boolean;
}

export interface ISidebarItemAction {
  id: string;
  label: string;
  icon: React.ReactNode;
  onClick: (itemId: string, value?: string) => void;
  className?: string;
  disabled?: boolean;
}

interface SidebarItemProps {
  item: ISidebarItem;
  isSelected: boolean;
  disabled?: boolean;
  onSelect: (item: ISidebarItem) => void;
  itemActions?: ISidebarItemAction[];
  iconClassName?: string;
  className?: string;
  warning?: boolean;
}

const SidebarItem = ({
  item,
  isSelected,
  disabled,
  onSelect,
  itemActions = [],
  iconClassName,
  className = "",
  warning,
}: SidebarItemProps) => {
  const [isRenaming, setIsRenaming] = useState(false);
  const renameInputRef = useRef<HTMLInputElement>(null);
  const renameAction = itemActions.find((action) => action.id === "rename");
  const clickTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Focus and select text immediately when renaming starts
  React.useEffect(() => {
    if (isRenaming && renameInputRef.current) {
      // Use requestAnimationFrame to ensure DOM is ready, then immediately focus and select
      requestAnimationFrame(() => {
        if (renameInputRef.current) {
          renameInputRef.current.focus();
          renameInputRef.current.select();
        }
      });

      const handleClickOutside = (event: MouseEvent) => {
        if (
          renameInputRef.current &&
          !renameInputRef.current.contains(event.target as Node)
        ) {
          if (renameAction && item.id && renameInputRef.current.value) {
            renameAction.onClick(item.id, renameInputRef.current.value.trim());
            setIsRenaming(false);
          }
        }
      };

      document.addEventListener("mousedown", handleClickOutside);
      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isRenaming]);

  // Helper to clear click timeout
  const clearClickTimeout = () => {
    if (clickTimeoutRef.current) {
      clearTimeout(clickTimeoutRef.current);
      clickTimeoutRef.current = null;
    }
  };

  const handleClick = () => {
    if (disabled) return;
    clearClickTimeout();
    // Delay selection to allow double-click detection
    clickTimeoutRef.current = setTimeout(() => onSelect(item), 250);
  };

  const handleDoubleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    clearClickTimeout();
    if (disabled || !renameAction) return;
    setIsRenaming(true);
  };

  // Cleanup on unmount
  React.useEffect(() => () => clearClickTimeout(), []);

  return (
    <div
      className={cn(
        "flex items-center h-[32px] gap-[6px] px-[8px] py-[6px] rounded-[8px] group cursor-pointer self-stretch",
        className,
        isSelected ? "bg-[#F6F6F6]" : "hover:bg-[#F6F6F699]",
        disabled && "opacity-50 cursor-not-allowed hover:bg-transparent",
      )}
      onClick={handleClick}
      onDoubleClick={handleDoubleClick}
    >
      <div className="flex items-center justify-center">
        <item.icon
          size={14}
          className={cn(
            "shrink-0",
            item.type === "button" ? "text-brand-primary-800" : "text-brand-primary-400",
            isSelected
              ? item.type === "tab"
                ? "text-brand-orange"
                : "text-brand-primary-600"
              : "",
            disabled && "text-brand-primary-400",
            iconClassName,
          )}
        />
      </div>
      {!isRenaming ? (
        <Tooltip>
          <TooltipTrigger asChild>
            <span
              className={cn(
                "text-[13px] leading-[19.16px] tracking-[0.02em] truncate cursor-pointer translate-y-[1px]",
                item.type === "button"
                  ? "text-brand-primary-800"
                  : "text-brand-primary-600",
                isSelected
                  ? "text-brand-primary-800 font-medium"
                  : "text-brand-primary-600 font-normal",
                disabled && "text-brand-primary-600",
              )}
            >
              {item.label}
            </span>
          </TooltipTrigger>
          <TooltipContent align="start">{item.label}</TooltipContent>
        </Tooltip>
      ) : (
        <Input
          ref={renameInputRef}
          defaultValue={item.label}
          className="w-full h-[26px] py-0 px-1 border-0 rounded-[4px] text-[13px] font-normal text-brand-primary-800 focus:outline-none shadow-none"
          onKeyDown={(e) => {
            e.stopPropagation();
            if (e.key === "Enter" && item.id && renameInputRef.current?.value) {
              renameAction?.onClick(item.id, renameInputRef.current.value.trim());
              setIsRenaming(false);
            } else if (e.key === "Escape") {
              setIsRenaming(false);
            }
          }}
          onClick={(e) => {
            e.stopPropagation();
          }}
          onBlur={() => {
            if (item.id && renameInputRef.current?.value) {
              renameAction?.onClick(item.id, renameInputRef.current.value.trim());
              setIsRenaming(false);
            }
          }}
        />
      )}

      {/* Item Actions */}
      {itemActions.length > 0 && (
        <DropdownMenu>
          <DropdownMenuTrigger
            asChild
            className="group-hover:opacity-100 data-[state=open]:opacity-100 opacity-0 shrink-0"
          >
            <MoreVertical
              className="stroke-[1.5] ml-auto fill-black cursor-pointer shrink-0"
              size={18}
            />
          </DropdownMenuTrigger>
          <DropdownMenuContent
            align="start"
            side="right"
            className="p-0 rounded-[4px] bg-[#FCFCF9]"
          >
            {itemActions.map((action) => (
              <DropdownMenuItem
                key={action.id}
                onClick={(e) => {
                  e.stopPropagation();
                  if (action.id === "rename") {
                    setIsRenaming(true);
                  } else {
                    action.onClick(item.id || "");
                  }
                }}
                disabled={action.disabled}
                className={cn(
                  "px-[9px] py-[10px] border-0 !rounded-none hover:!rounded-none hover:bg-[#F7F7F3] aria-selected:bg-transparent flex items-center gap-2",
                  action.className,
                  action.disabled && "opacity-50 cursor-not-allowed",
                )}
              >
                {action.icon}
                <span className="h-[14px]">{action.label}</span>
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      )}
      {item?.beta && (
        <div className="ml-auto h-5 text-[#FF4500CC] font-medium text-[10px] leading-[19.16px] tracking-[0.02em] p-[6px] squircle squircle-fill-[#FF450014] squircle-radius-[6px] squircle-smooth-[1] flex items-center">
          Beta
        </div>
      )}
      {warning && (
        <div
          className="ml-auto
            rounded-[2px] bg-[#FFF0F0]
            flex justify-center items-center
            w-[18px] h-[18px] aspect-square"
        >
          <RiErrorWarningLine size={14} className="text-destructive-700 shrink-0" />
        </div>
      )}
    </div>
  );
};

export default SidebarItem;
